import { Datacenter, datadogLogs } from "@screencloud/datadog-browser-logs";
import { Context } from "@datadog/browser-core";
import { Maybe } from "../queries";
import { actualUtcNow, TimeOptions } from "../utils/timeManager";
import { playbackNowTimestamp } from "../utils/timeManager";
import { LogLevel, LogOutput, RemoteLoggerConfig } from "./logger";

interface Event {
  level: LogLevel;
  message: string;
  context?: Context;
}

type EventQueues = Event[];

export interface GetLogger {
  debug: (message: string, context?: Context) => void;
  info: (message: string, context?: Context) => void;
  warn: (message: string, context?: Context) => void;
  error: (message: string, context?: Context) => void;
}

class DataDogLogger {
  public isInitialized = false;
  public isLokiLoggingInitialized = false;

  private isEnableRemoteLogger = false;
  private timeOptions: TimeOptions = {
    timeOffset: 0,
    timelineControlOffset: 0,
  };

  // using for store the events that are called before initialized
  private eventQueues: EventQueues = [];

  public init = (
    config: RemoteLoggerConfig,
    featureFlags: Maybe<string>[]
  ): void => {
    this.isEnableRemoteLogger = this.shouldEnableRemoteLogger(featureFlags);

    if (this.isInitialized) return;

    // always initializ datadogLogs for capture error events
    datadogLogs.init({
      clientToken: config.clientToken,
      datacenter: Datacenter.US, // using only US datadog account for convenience right now
      env: config.env,
      forwardErrorsToLogs: true, //to always log errors
      version: config.version,
      proxyHost: config.proxyHost,
      service: "studio-player",
    });

    datadogLogs.logger.addContext("platform", config.platform);
    datadogLogs.logger.addContext("deviceId", config.deviceId);
    datadogLogs.logger.addContext("screenId", config.screenId);
    datadogLogs.logger.addContext("orgId", config.orgId);

    this.timeOptions = config.timeOptions;
    this.isInitialized = true;
    this.flushEventQueues();

    // register event to flush buffer when back online
    window.addEventListener("online", () => this.flushEventQueues());
  };

  public log = (level: LogLevel, message: string, context?: Context): void => {
    // push logs to event queue when offline
    if (!!navigator && !navigator?.onLine) {
      this.addEventToQueues({
        level,
        message: "[offline]" + message,
        context,
      });
      return;
    }

    /**
     * handle when events are sent before initialized
     * need to add an event to the event queues to make it be able to sync later
     */

    /**
     * only push datadog events here
     * */
    if (!this.isInitialized) {
      this.addEventToQueues({
        level,
        message,
        context,
      });
      return;
    }

    this.eventLogger(level, message, {
      ...context,
      timestamp: actualUtcNow(this.timeOptions), // start using UTC Time
    });
  };

  public shouldEnableRemoteLogger = (
    featureFlags: Maybe<string>[]
  ): boolean => {
    return featureFlags.includes("player_log_enable");
  };

  private addEventToQueues = ({ level, message, context }: Event): void => {
    const eventQueuesLimit = parseInt(
      process.env.REACT_APP_EVENT_QUEUES_LIMIT || "100"
    );

    // need to have a limitation of event stack because it could effect a memory leak
    if (this.eventQueues.length > eventQueuesLimit) {
      this.eventQueues.shift();
    }

    this.eventQueues.push({
      level,
      message,
      context: {
        ...context,
        timestamp: playbackNowTimestamp(this.timeOptions),
      },
    });
  };

  private flushEventQueues = (): void => {
    this.eventQueues.forEach((event) => {
      this.eventLogger(event.level, event.message, event.context);
    });
    this.clearEventQueues();
  };

  private clearEventQueues = (): void => {
    this.eventQueues = [];
  };

  private eventLogger = (
    level: LogLevel,
    message: string,
    context?: Context
  ): void => {
    if (!this.isInitialized || !this.isEnableRemoteLogger) {
      return;
    }

    switch (level) {
      case 4:
        this.isEnableRemoteLogger && datadogLogs.logger.debug(message, context);
        break;
      case 3:
        this.isEnableRemoteLogger && datadogLogs.logger.info(message, context);
        break;
      case 2:
        this.isEnableRemoteLogger && datadogLogs.logger.warn(message, context);
        break;
      case 1:
        // case error always send data to data dog for debugging
        datadogLogs.logger.error(message, context);
        break;
    }
  };

  public setTimeOptions = (timeOptions: TimeOptions): void => {
    this.timeOptions = timeOptions;
  };

  public getEventQueues = (): EventQueues => {
    return this.eventQueues;
  };

  public getLogger = (): LogOutput => {
    return this.log;
  };
}

export default new DataDogLogger();
